import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/20/solid';
import { isValidPhoneNumber } from 'libphonenumber-js';
import Link from 'next/link';
import { redirect, useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import trpc from '@/lib/trpc';

type FormFieldValues = {
  companyName: string;
  companyRegNo: string;
  industrySector: string | null;
  note: string | null;
  numberOfEmployees: number | null;
  personEmailAddress: string;
  personName: string;
  personPhoneNumber: string;
};

type SaveRegistrationHandler = (data: FormFieldValues) => Promise<any>;

export default function ContentRegistrationPage() {
  const router = useRouter();
  const saveRegistrationData = trpc.registration.useMutation();

  const handleSaveRegistration = useCallback<SaveRegistrationHandler>(
    (formData) =>
      new Promise((resolve, reject) => {
        saveRegistrationData.mutate(
          {
            companyName: formData.companyName,
            companyRegNo: formData.companyRegNo,
            industrySector: formData.industrySector,
            note: formData.note,
            numberOfEmployees:
              typeof formData.numberOfEmployees === 'number' && !isNaN(formData.numberOfEmployees)
                ? formData.numberOfEmployees
                : null,
            personEmailAddress: formData.personEmailAddress,
            personName: formData.personName,
            personPhoneNumber: formData.personPhoneNumber,
          },
          {
            onError(err) {
              reject(err);
            },
            onSuccess(data) {
              resolve(data);
              router.push('/registrace/odeslano');
            },
          },
        );
      }),
    [router, saveRegistrationData],
  );

  return (
    <div className='grid grid-cols-1 py-10 lg:grid-cols-3'>
      <div className='bg-primary overflow-hidden rounded-lg py-10 px-6 sm:px-10 xl:p-12'>
        <h3 className='text-lg font-medium text-white'>Potřebujete více informací?</h3>
        <p className='text-mask-contrast mt-6 max-w-3xl text-base'>
          {'Pokud potřebujete získat více informací k registraci, prosím obraťte se na níže uvedené kontakty:'}
        </p>
        <dl className='mt-8 space-y-6'>
          <dt>
            <span className='sr-only'>Zákaznická linka</span>
          </dt>
          <dd className='text-mask-contrast text-base'>
            <a className='hover:text-primary-focus flex' href='tel:+420272099544'>
              <PhoneIcon aria-hidden='true' className='h-6 w-6 shrink-0' />
              <span className='ml-3'>{'+420 272 099 544'}</span>
            </a>
          </dd>
          <dt>
            <span className='sr-only'>Email</span>
          </dt>
          <dd className='text-mask-contrast text-base'>
            <a className='hover:text-primary-focus flex' href='mailto:prodej@blyth.cz'>
              <EnvelopeIcon aria-hidden='true' className='h-6 w-6 shrink-0' />
              <span className='ml-3'>{'prodej@blyth.cz'}</span>
            </a>
          </dd>
        </dl>
        <div className='my-8'>
          <hr className='text-mask-contrast' />
        </div>
        <p className='text-mask-contrast mt-6 max-w-3xl text-base'>
          <Link className='hover:text-primary-focus underline' href='/stranky/obchodni-podminky' prefetch={false}>
            {'Obchodní podmínky'}
          </Link>
        </p>
        <p className='text-mask-contrast mt-6 max-w-3xl text-base'>
          <Link
            className='hover:text-primary-focus underline'
            href='/stranky/zasady-ochrany-osobnich-udaju'
            prefetch={false}
          >
            {'Zásady ochrany osobních údajů'}
          </Link>
        </p>
      </div>
      <div className='py-10 px-6 sm:px-10 lg:col-span-2 xl:p-12'>
        <ContentRegistrationPageFormContent saveRegistration={handleSaveRegistration} />
      </div>
    </div>
  );
}

// *****************************************
// *
// * ContentRegistrationPageFormContent
// *
// *****************************************

function ContentRegistrationPageFormContent({ saveRegistration }: { saveRegistration: SaveRegistrationHandler }) {
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<FormFieldValues>({
    defaultValues: {
      companyName: '',
      companyRegNo: '',
      industrySector: null,
      note: null,
      numberOfEmployees: null,
      personEmailAddress: '',
      personName: '',
      personPhoneNumber: '',
    },
    mode: 'onBlur',
  });

  return (
    <>
      <h3 className='text-warm-gray-900 text-lg font-medium'>{'Odešlete žádost o registraci'}</h3>
      <form
        className='form-styles mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8'
        onSubmit={handleSubmit(saveRegistration)}
      >
        <div className='sm:col-span-2'>
          <div>
            <label htmlFor='companyName'>
              {'Název společnosti'}
              <abbr>*</abbr>
            </label>
            <div data-role='input'>
              <input
                autoComplete='organization'
                id='companyName'
                placeholder=''
                type='text'
                {...register('companyName', {
                  required: true,
                })}
                aria-errormessage={errors?.companyName?.type === 'required' ? 'companyName-err-required' : undefined}
                aria-invalid={errors?.companyName ? 'true' : 'false'}
              />
            </div>
            {errors.companyName?.type === 'required' && (
              <p id={'companyName-err-required'} role='alert'>
                {'Pole je povinné.'}
              </p>
            )}
          </div>
        </div>
        <div>
          <label htmlFor='companyRegNo'>
            {'IČO'}
            <abbr>*</abbr>
          </label>
          <div data-role='input'>
            <input
              autoComplete='off'
              id='companyRegNo'
              placeholder='IČO'
              type='text'
              {...register('companyRegNo', {
                minLength: 5,
                required: true,
              })}
              aria-errormessage={errors?.companyRegNo?.type === 'required' ? 'companyRegNo-err-required' : undefined}
              aria-invalid={errors?.companyRegNo ? 'true' : 'false'}
            />
          </div>
          {errors.companyRegNo?.type === 'required' && (
            <p id={'companyRegNo-err-required'} role='alert'>
              {'Pole je povinné.'}
            </p>
          )}
        </div>
        <div className='sm:col-span-2'>
          <div>
            <label htmlFor='personName'>
              {'Jméno Příjmení'}
              <abbr>*</abbr>
            </label>
            <div data-role='input'>
              <input
                autoComplete='name'
                id='personName'
                placeholder='Jan Novotný'
                type='text'
                {...register('personName', {
                  required: true,
                })}
                aria-errormessage={errors?.personName?.type === 'required' ? 'personName-err-required' : undefined}
                aria-invalid={errors?.personName ? 'true' : 'false'}
              />
            </div>
            {errors.personName?.type === 'required' && (
              <p id={'personName-err-required'} role='alert'>
                {'Pole je povinné.'}
              </p>
            )}
          </div>
        </div>
        <div>
          <label htmlFor='personEmailAddress'>
            {'Email'}
            <abbr>*</abbr>
          </label>
          <div className='relative' data-role='input-group'>
            <div data-role='adornment-start'>
              <EnvelopeIcon aria-hidden='true' className='h-5 w-5 text-gray-400' />
            </div>
            <input
              autoComplete='email'
              className='!pl-10'
              id='personEmailAddress'
              placeholder='vy@priklad.cz'
              type='email'
              {...register('personEmailAddress', {
                pattern:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/,
                required: true,
              })}
              aria-errormessage={
                errors?.personEmailAddress?.type === 'required'
                  ? 'personEmailAddress-err-required'
                  : errors?.personEmailAddress?.type === 'pattern'
                  ? 'personEmailAddress-err-format'
                  : undefined
              }
              aria-invalid={errors?.personEmailAddress ? 'true' : 'false'}
            />
          </div>
          {errors.personEmailAddress?.type === 'required' && (
            <p id={'personEmailAddress-err-required'} role='alert'>
              {'Pole je povinné.'}
            </p>
          )}
          {errors.personEmailAddress?.type === 'pattern' && (
            <p id={'personEmailAddress-err-format'} role='alert'>
              {'Neplatný formát email adresy.'}
            </p>
          )}
        </div>
        <div>
          <label htmlFor='personPhoneNumber'>
            {'Telefon'}
            <abbr>*</abbr>
          </label>
          <div className='relative' data-role='input-group'>
            <div data-role='adornment-start'>
              <PhoneIcon aria-hidden='true' className='h-5 w-5' />
            </div>
            <input
              autoComplete='tel'
              className='!pl-10'
              id='personPhoneNumber'
              placeholder='+420123456789'
              type='text'
              {...register('personPhoneNumber', {
                validate: {
                  phoneNumber: (v: string | null | undefined) => isValidPhoneNumber(v || ''),
                  required: (v: string | null | undefined) => !!v,
                },
              })}
              aria-errormessage={
                errors?.personPhoneNumber?.type === 'required'
                  ? 'personPhoneNumber-err-required'
                  : errors?.personPhoneNumber?.type === 'phoneNumber'
                  ? 'personPhoneNumber-err-format'
                  : undefined
              }
              aria-invalid={errors?.personPhoneNumber ? 'true' : 'false'}
            />
          </div>
          {errors.personPhoneNumber?.type === 'required' && (
            <p id={'personPhoneNumber-err-required'} role='alert'>
              {'Pole je povinné.'}
            </p>
          )}
          {errors.personPhoneNumber?.type === 'phoneNumber' && (
            <p id={'personPhoneNumber-err-format'} role='alert'>
              {'Neplatné telefonní číslo.'}
            </p>
          )}
        </div>
        <div>
          <label htmlFor='industrySector'>{'Odvětví, ve kterém pracujete'}</label>
          <div data-role='input'>
            <input
              autoComplete='off'
              id='industrySector'
              placeholder=''
              type='text'
              {...register('industrySector', {
                required: false,
              })}
            />
          </div>
        </div>
        <div>
          <label htmlFor='numberOfEmployees'>{'Počet zaměstnanců'}</label>
          <div data-role='input'>
            <input
              autoComplete='off'
              id='numberOfEmployees'
              placeholder=''
              type='number'
              {...register('numberOfEmployees', {
                required: false,
                valueAsNumber: true,
              })}
            />
          </div>
        </div>
        <div className='sm:col-span-2'>
          <div>
            <label htmlFor='note'>{'Poznámka'}</label>
            <div data-role='input'>
              <textarea
                autoComplete='off'
                id='note'
                placeholder=''
                {...register('note', {
                  required: false,
                })}
                rows={4}
              />
            </div>
          </div>
        </div>
        <div className='mt-8 text-right sm:col-span-2'>
          <button className='btn btn-primary' type='submit'>
            {'Registrovat se'}
          </button>
        </div>
      </form>
    </>
  );
}
